import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { CrossSellAPI } from '@share/api';
import type { ICarLocationParam } from '@share/api';
import type { CrossSellOrigin, CrossSellProduct } from '@share/common-types';
import { CROSS_SELL_VERTICAL_TYPE, OFFERED_VERTICALS } from '@share/utils';
import type { ICrossSellEntry } from '@share/utils';

import type { ICarLocationsState } from './cars-locations';

/**
 * Cross-sell prefetch cache (TRAV23-4194).
 *
 * The recommendations call is slow, so we kick it off early (hotel/condo at
 * recheck time, cars on "reserve") and cache the result here. To make it faster
 * we fire one call per offered vertical in parallel, using the backend `Type`
 * selector — e.g. a hotel booking calls for cars + activities; a car booking for
 * hotels + condos + activities. The backend already applies the enable/disable
 * rules, so a disabled vertical just returns nothing — no front-end gating.
 *
 * The section is still only rendered on the confirmation screen, so nothing is
 * shown to the user until the booking has completed.
 */

export interface ICrossSellPrefetchArgs {
  origin: CrossSellOrigin;
  /** Hotel / condo bookings. */
  propertyId?: string | number;
  /** Car bookings (pick-up / drop-off locations). */
  carLocations?: ICarLocationParam[];
  checkIn?: string;
  checkOut?: string;
}

export interface ICrossSellState {
  loading: boolean;
  /** Signature of the cached request, so consumers only use a matching result. */
  key: string | null;
  entries: ICrossSellEntry[] | null;
}

const initialState: ICrossSellState = {
  loading: false,
  key: null,
  entries: null,
};

/** Stable signature for a prefetch request; used to de-dupe and to match cached results. */
export const crossSellKey = ({ origin, propertyId, carLocations, checkIn, checkOut }: ICrossSellPrefetchArgs): string => {
  const locations = (carLocations ?? []).map((loc) => `${loc.id}:${loc.kind}`).join(',');
  return `${origin}|${propertyId ?? ''}|${locations}|${checkIn ?? ''}|${checkOut ?? ''}`;
};

/**
 * Shared derivation of the car cross-sell locations from the cars-locations store,
 * so the click-time prefetch and the confirmation screen produce the exact same
 * request key.
 */
export const getCarCrossSellLocations = (carsLocationsStore?: ICarLocationsState): ICarLocationParam[] => {
  const toParam = (location?: { code?: number; type?: string; realType?: string } | null): ICarLocationParam | null => {
    if (!location?.code) return null;
    const rawKind = String(location.realType || location.type || '').toLowerCase();
    return { id: location.code, kind: rawKind.includes('airport') ? 'airport' : 'city' };
  };
  return [
    toParam(carsLocationsStore?.pickUp?.location),
    toParam(carsLocationsStore?.dropOff?.location),
  ].filter((loc, index, all): loc is ICarLocationParam =>
    !!loc && all.findIndex((other) => other?.id === loc.id) === index,
  );
};

const isValidPropertyId = (propertyId?: string | number): boolean =>
  propertyId != null && String(propertyId) !== '' && String(propertyId) !== 'NaN' && Number(propertyId) > 0;

/** Whether the args carry everything the API needs. */
const hasEnoughToFetch = (args: ICrossSellPrefetchArgs): boolean => {
  if (!args.checkIn || !args.checkOut) return false;
  if (args.origin === 'car') return !!(args.carLocations && args.carLocations.length);
  return isValidPropertyId(args.propertyId);
};

/** Fetches a single offered vertical from the booked product's anchor endpoint. */
const fetchVertical = async (
  args: ICrossSellPrefetchArgs,
  vertical: CrossSellProduct,
): Promise<ICrossSellEntry | null> => {
  const type = CROSS_SELL_VERTICAL_TYPE[vertical];
  try {
    if (args.origin === 'hotel') {
      const { data } = await CrossSellAPI.getHotelCrossSell({ propertyId: args.propertyId, checkIn: args.checkIn!, checkOut: args.checkOut!, type });
      return data ? { vertical, response: data } : null;
    }
    if (args.origin === 'condo') {
      const { data } = await CrossSellAPI.getCondoCrossSell({ propertyId: args.propertyId, checkIn: args.checkIn!, checkOut: args.checkOut!, type });
      return data ? { vertical, response: data } : null;
    }
    if (args.origin === 'car') {
      const { data } = await CrossSellAPI.getCarsCrossSell({ locations: args.carLocations!, includeReturnLocation: true, checkIn: args.checkIn!, checkOut: args.checkOut!, type });
      return data ? { vertical, response: data } : null;
    }
  } catch {
    return null;
  }
  return null;
};

export const prefetchCrossSell = createAsyncThunk(
  'crossSell/prefetch',
  async (args: ICrossSellPrefetchArgs): Promise<ICrossSellEntry[]> => {
    const results = await Promise.all(
      OFFERED_VERTICALS[args.origin].map((vertical) => fetchVertical(args, vertical)),
    );
    return results.filter((entry): entry is ICrossSellEntry => entry !== null);
  },
  {
    // Skip when we don't have enough to fetch, or the same request is already cached / in flight.
    condition: (args, { getState }) => {
      if (!hasEnoughToFetch(args)) return false;
      const { crossSellStore } = getState() as { crossSellStore: ICrossSellState };
      const key = crossSellKey(args);
      if (crossSellStore.key === key && (crossSellStore.loading || crossSellStore.entries !== null)) {
        return false;
      }
      return true;
    },
  },
);

const crossSellSlice = createSlice({
  name: 'crossSell',
  initialState,
  reducers: {
    resetCrossSell: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(prefetchCrossSell.pending, (state, action) => {
        state.loading = true;
        state.key = crossSellKey(action.meta.arg);
        state.entries = null;
      })
      .addCase(prefetchCrossSell.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload;
      })
      .addCase(prefetchCrossSell.rejected, (state) => {
        // On failure the section is simply omitted; the rest of the page is unaffected.
        state.loading = false;
        state.entries = [];
      });
  },
});

export const { resetCrossSell } = crossSellSlice.actions;

export const crossSellReducer = crossSellSlice.reducer;
